import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Wrench } from 'lucide-react';
import ConfirmDeleteButton from './ConfirmDeleteButton';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import AdminModal from './AdminModal';
import { adminPanelPlaceholderSx, AdminPanelLoading } from './adminPanelContent';

export default function AiToolsPanel({ getToken }) {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE_URL}/api/ai-tools`, {
        headers: await getAuthHeaders(getToken)
      });
      if (!res.ok) throw new Error('Failed to load tools');
      const data = await res.json();
      setTools(data.tools || []);
    } catch (e) {
      setError(e.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setName('');
    setDescription('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSubmitting(false);
  };

  const submitAdd = async () => {
    const n = name.trim();
    if (!n) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai-tools`, {
        method: 'POST',
        headers: { ...(await getAuthHeaders(getToken)), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: n, description: description.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Create failed');
      closeDialog();
      await load();
    } catch (e) {
      setError(e.message || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (toolId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai-tools/${encodeURIComponent(toolId)}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(getToken)
      });
      if (!res.ok && res.status !== 204) throw new Error('Delete failed');
      await load();
    } catch (e) {
      setError(e.message || 'Delete failed');
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <Box sx={{ color: 'warning.light', display: 'flex', flexShrink: 0, alignItems: 'center' }}>
            <Wrench size={22} strokeWidth={2} />
          </Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            AI tools
          </Typography>
        </Box>
        <Button size="small" variant="contained" onClick={openAdd} disabled={loading}>
          Add tool
        </Button>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <AdminModal
        open={dialogOpen}
        onClose={closeDialog}
        title="Add AI tool"
        maxWidth="xs"
        actions={
          <>
            <Button onClick={closeDialog} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="contained" onClick={submitAdd} disabled={!name.trim() || submitting}>
              {submitting ? 'Saving…' : 'Add'}
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder="e.g. Weather API"
          />
          <TextField
            label="Description"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this tool does (for humans / future schema)"
          />
        </Box>
      </AdminModal>

      {loading ? (
        <AdminPanelLoading label="Loading tools…" />
      ) : tools.length === 0 ? (
        <Box sx={adminPanelPlaceholderSx}>
          <Typography variant="body2" color="text.secondary">
            No tools yet. Tools appear in the workflow editor palette and can wire into Text Generation nodes.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right" width={56} />
              </TableRow>
            </TableHead>
            <TableBody>
              {tools.map((t) => (
                <TableRow key={t.id}>
                  <TableCell sx={{ fontWeight: 600 }}>{t.name}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', maxWidth: 360 }}>{t.description || '—'}</TableCell>
                  <TableCell align="right">
                    <ConfirmDeleteButton onConfirm={() => remove(t.id)} iconSize={16} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
